import { useMemo } from 'react';
import theme from '../theme';
import Tooltip from '../tooltip';

import ReactDOMServer from 'react-dom/server';
import { roundMaxToUpperZero, roundMinToLowerZero } from './common-functions';

interface Use3DOptionsProps {
  data?: any[][];
  xAxisName?: string;
  yAxisName?: string;
  zAxisName?: string;
  min?: number;
  max?: number;
  showWireframe?: boolean;
  tooltip?: any & {
    xAxisTitle?: string;
    yAxisTitle?: string;
    zAxisTitle?: string;
    valueFormatter?: (value: any) => string;
    xAxisFormatter?: (value: any) => string;
    yAxisFormatter?: (value: any) => string;
    zAxisFormatter?: (value: any) => string;
  };
  xAxisData?: string[] | number[];
  yAxisData?: string[] | number[];
  visualMap?: any;
}

export const use3DOptions = ({
  data = [],
  xAxisName = '',
  yAxisName = '',
  zAxisName = '',
  min = 10,
  max = 80,
  showWireframe = true,
  tooltip,
  xAxisData = [],
  yAxisData = [],
  visualMap,
}: Use3DOptionsProps): any => {
  return useMemo(() => {
    const xTitle = tooltip?.xAxisTitle || xAxisName || 'X Axis';
    const yTitle = tooltip?.yAxisTitle || yAxisName || 'Y Axis';
    const zTitle = tooltip?.zAxisTitle || zAxisName || 'Z Axis';

    const xFormatter = tooltip?.xAxisFormatter || ((value: any) => value?.toString() || '');
    const yFormatter = tooltip?.yAxisFormatter || ((value: any) => value?.toString() || '');
    const zFormatter =
      tooltip?.zAxisFormatter ||
      tooltip?.valueFormatter ||
      ((value: any) => `${value?.toFixed(2)}%`);

    const option: any = {
      tooltip: {
        show: true,
        trigger: 'item',
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        formatter: (params: any) => {
          const xValue = xFormatter(params?.data[0]);
          const yValue = yFormatter(params?.data[1]);
          const zValue = zFormatter(params?.data[2]);

          const paramsArr = [
            { title: xTitle, value: xValue },
            { title: yTitle, value: yValue },
            { title: zTitle, value: zValue },
          ];

          const element = <Tooltip items={paramsArr} />;

          return ReactDOMServer.renderToStaticMarkup(element);
        },
        ...tooltip,
      },
      visualMap: visualMap || {
        ...theme.visualMap,
        min: min * 0.9,
        max: max * 1.1,
      },
      yAxis3D: {
        type: 'category',
        name: yAxisName,
        nameGap: 30,
        nameTextStyle: theme.nameTextStyle,
        axisPointer: theme.axisPointerConfig,
      },
      xAxis3D: {
        type: 'category',
        name: xAxisName,
        nameTextStyle: theme.nameTextStyle,
        nameGap: 15,
        axisPointer: theme.axisPointerConfig,
      },
      zAxis3D: {
        type: 'value',
        name: zAxisName,
        axisLabel: {
          formatter: (value: any) => (value !== 0 ? `${parseInt(value)}%` : ''),
        },
        nameTextStyle: theme.nameTextStyle,
        nameGap: 28,
        axisPointer: theme.axisPointerConfig,
        min: (v: any) => roundMinToLowerZero(v.min / 100) * 100,
        max: (v: any) => roundMaxToUpperZero(v.max / 100) * 100,
      },
      grid3D: {
        ...theme.grid3D,
        left: '0px',
        top: '-40px',
        right: '0px',
        bottom: '00px',
      },
      series: [
        {
          type: 'surface',
          wireframe: {
            show: showWireframe,
          },
          data,
        },
      ],
    };

    return option;
  }, [
    data,
    xAxisName,
    yAxisName,
    zAxisName,
    min,
    max,
    showWireframe,
    tooltip,
    xAxisData,
    yAxisData,
  ]);
};
